export interface DefinitionTerm {
  term: string;
  definition: string;
}

export interface DefinitionCategory {
  category: string;
  terms: DefinitionTerm[];
}

export const DEFINITIONS: DefinitionCategory[] = [
  {
    category: 'Insights',
    terms: [
      {
        term: 'Hottest Slots',
        definition:
          'Shows the games with the highest RTP (Return to Player) over a selected period (e.g., last 7 days). RTP is calculated as $ Won ÷ $ Played and sorted in descending order.',
      },
      {
        term: 'Coldest Slots',
        definition:
          'Shows the games with the lowest RTP (Return to Player) over a selected period. RTP is calculated as $ Won ÷ $ Played and sorted in ascending order.',
      },
      {
        term: 'Popular',
        definition:
          'Shows the most-played games over a selected period, based on total spins. Spins are sorted in descending order.',
      },
      {
        term: 'Jackpots',
        definition:
          'This insight is the total amount of money won from hand-pay jackpots – typically $2,000 or more.',
      },
      {
        term: '$ Won',
        definition:
          'This insight and statistic shows how much a game has paid out to the player over a selected time frame regardless of how much money was bet or played.',
      },
      {
        term: '$ Played',
        definition:
          'Shows the total amount wagered over a selected period. This may include wallet funds, winnings from previous spins, and free play.',
      },
      {
        term: 'Win / Spin',
        definition:
          'Shows the average amount won per spin over a selected period. W/S is calculated as $ Won ÷ Spins.',
      },
      {
        term: 'Jackpot Win / Spin',
        definition:
          'Shows the average jackpot payout per spin over a selected period. JW/S is calculated as total jackpot winnings ÷ Spins.',
      },
      {
        term: '$ Played since last Jackpot',
        definition:
          'Shows the amount wagered since the most recent jackpot was paid. Games without a jackpot in the past 12 months are excluded.',
      },
      {
        term: 'Win / Spin without Jackpots',
        definition:
          'Shows the average amount won per spin excluding jackpot payouts. W/S-J is calculated as ($ Won − Jackpots) ÷ Spins.',
      },
    ],
  },
  {
    category: 'Stats',
    terms: [
      {
        term: 'RTP',
        definition:
          'Shows the games with the highest or lowest RTP (Return to Player) over a selected period. RTP is calculated as $ Won ÷ $ Played and sorted in descending order for Hottest Slots or ascending order for Coldest Slots.',
      },
      {
        term: 'Spins',
        definition:
          'Shows the total number of spins for each game over a selected period. Sorted in descending order, it displays the Most Popular slots; sorted in ascending order, it shows the least popular or newest games.',
      },
      {
        term: '$ Played',
        definition:
          'Shows how much money has been played since the last jackpot payout over a selected period. Excludes games with no jackpot in the last 12 months and sorts by $ Played in descending order.',
      },
      {
        term: '$ Won',
        definition:
          'Shows the total money won from hand-pay jackpots (typically $2,000 or more) over a selected period. Displays the total jackpot winnings and number of jackpots hit, sorted in descending order.',
      },
      {
        term: 'W/S',
        definition:
          'Shows how much a game pays out per spin over a selected period. Win/Spin (W/S) is calculated as $ Won ÷ Number of Spins and expressed as a dollar amount ($).',
      },
      {
        term: 'JW/S',
        definition:
          'Shows the average jackpot winnings per spin over a selected period. Jackpot Win/Spin is calculated as Jackpot Winnings ÷ Number of Spins and expressed as a dollar amount ($).',
      },
      {
        term: 'W/S-J',
        definition:
          'Shows the average payout per spin excluding jackpots over a selected period. Win/Spin without Jackpots is calculated as ($ Won − Jackpot Winnings) ÷ Number of Spins and expressed as a dollar amount ($).',
      },
    ],
  },
  {
    category: 'Game Info',
    terms: [
      {
        term: 'Manufacturer',
        definition:
          'Shows the original equipment manufacturer (OEM) that produced each game. Manufacturers may appear under abbreviated names, such as LW or L&W for Light & Wonder.',
      },
      {
        term: 'Bank',
        definition:
          'Slot machines are typically grouped together in a line, circle, square, triangle, or rectangle. This grouping is referred to as the "bank" or bank of machines.',
      },
      {
        term: 'Slot',
        definition:
          'This generic term describes any electronic gaming machine (EGM) or electronic table game (ETG).',
      },
      {
        term: 'POP',
        definition:
          'Shows the theoretical payout percentage (POP) each game is designed to return over its lifetime. POP is calculated as $ Won ÷ $ Played and represents the expected return, while RTP reflects the actual return over a selected period.',
      },
      {
        term: 'Volatility',
        definition:
          "Volatility describes how uneven a slot's payouts are: low volatility pays smaller amounts more often, high volatility pays larger amounts less often, for a similar long-run return.",
      },
      {
        term: 'Denom',
        definition:
          'Shows the denomination (Denom) or value of one credit for each game. For example, a penny denom equals $0.01 per credit, while a dollar denom equals $1.00. Multi-denom machines allow different credit values to be selected.',
      },
    ],
  },
  {
    category: 'Collections',
    terms: [
      {
        term: 'All Games',
        definition: 'Shows all slots on the gaming floor being tracked by Slot Check.',
      },
      {
        term: 'Playlist',
        definition:
          'A folder in which you can organize slots that you want to analyze and play. This helps players plan their play.',
      },
      {
        term: 'Favorites',
        definition:
          'A folder in which you can organize your favorite slots so you can easily follow and analyze them.',
      },
      {
        term: 'Video Poker',
        definition:
          'Selecting this narrows the available slots to those that are video poker games.',
      },
      {
        term: 'Progressive',
        definition:
          'Selecting this narrows the available slots to those that are progressive – wide area, local, and linear.',
      },
      {
        term: 'Multi Game',
        definition:
          'Selecting this narrows the available slots to those that are multi-game.',
      },
      {
        term: 'Best',
        definition:
          'Selecting this narrows the available slots to those Slot Check considers the best-performing based on RTP over the selected period.',
      },
      {
        term: 'Recent',
        definition:
          'Shows the games that have been played in the previous 30 days from your most recent visit.',
      },
      {
        term: 'New Slots',
        definition:
          'Slots that have been on the gaming floor for 100 days or less.',
      },
    ],
  },
  {
    category: 'Miscellaneous',
    terms: [
      {
        term: 'Misc',
        definition:
          'Shows the original equipment manufacturer (OEM) that produced each game. Manufacturers may appear under abbreviated names, such as LW or L&W for Light & Wonder.',
      }
    ],
  },
];
